import React, { useEffect } from "react";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  useGetLate,
  useUpdateInvoice,
} from "modules/invoice/api/src/invoice.api";
import { useNavigate } from "react-router-dom";
import { MdArrowLeft, MdArrowRight } from "react-icons/md";
import { ExternalLinkIcon, PhoneIcon } from "lucide-react";
import SearchAndFilterBar from "views/admin/default/components/filter/searchAndFilterBar";

type RowObj = {
  _id: string;
  clientId: string;
  clientName: string;
  createdAt: Date;
  dueDate: Date;
  total: number;
  dueAmount: number;
  lastContact: Date;
};

const columnHelper = createColumnHelper<RowObj>();

const formatCurrency = (v: number) =>
  v.toLocaleString("fr-FR", { style: "currency", currency: "EUR" });

export const LateInvoiceTable = () => {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const navigate = useNavigate();
  const [searchName, setSearchName] = React.useState("");
  const [filters, setFilters] = React.useState<any>({});
  const { data, isLoading, fetchNextPage, hasNextPage, refetch } = useGetLate(
    searchName,
    filters
  );
  const { mutate: updateInvoice } = useUpdateInvoice(undefined, true);

  useEffect(() => {
    refetch();
  }, [filters, refetch]);

  const flatData = React.useMemo(
    () => data?.pages?.[currentPage - 1]?.data ?? [],
    [data?.pages, currentPage]
  );
  const totalPage = data?.pages[0]?.count ?? 0;

  const handleMarkAsContacted = (invoiceId: string) => {
    const invoice = flatData.find((inv) => inv._id === invoiceId);
    if (!invoice) return;
    updateInvoice({ ...invoice, lastContact: new Date() });
  };

  const columns = [
    columnHelper.accessor("createdAt", {
      id: "Date",
      header: () => <span>Date</span>,
      cell: (info) => (
        <span className="text-gray-600">
          {new Date(info.getValue()).toLocaleDateString("fr-FR")}
        </span>
      ),
    }),
    columnHelper.accessor("clientName", {
      id: "Client",
      header: () => <span>Client</span>,
      cell: (info) => (
        <span className="font-medium text-gray-900">{info.getValue()}</span>
      ),
    }),
    columnHelper.accessor("total", {
      id: "Montant",
      header: () => <span>Montant</span>,
      cell: (info) => (
        <span className="font-medium text-gray-900">
          {formatCurrency(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor("dueAmount", {
      id: "Reste",
      header: () => <span>Reste</span>,
      cell: (info) => (
        <span className="inline-flex rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
          {formatCurrency(info.getValue())}
        </span>
      ),
    }),
    columnHelper.accessor(
      (row) => {
        const diffTime = Math.abs(
          new Date().getTime() - new Date(row.dueDate).getTime()
        );
        return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      },
      {
        id: "Retard",
        header: () => <span>Retard</span>,
        cell: (info) => {
          const days = info.getValue();
          return (
            <span
              className={`text-sm font-medium ${
                days > 90
                  ? "text-red-700"
                  : days > 30
                  ? "text-red-500"
                  : "text-amber-600"
              }`}
            >
              {days}j
            </span>
          );
        },
      }
    ),
    columnHelper.accessor("lastContact", {
      id: "Contact",
      header: () => <span>Contact</span>,
      cell: (info) => {
        const val = info.getValue();
        return val ? (
          <span className="text-gray-500">
            {new Date(val).toLocaleDateString("fr-FR")}
          </span>
        ) : (
          <span className="text-xs text-gray-300">Jamais</span>
        );
      },
    }),
    columnHelper.accessor("_id", {
      id: "actions",
      header: () => <span></span>,
      cell: (info) => (
        <div className="flex items-center gap-1">
          <button
            onClick={() => navigate(`/admin/invoice/${info.getValue()}`)}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
            title="Voir"
          >
            <ExternalLinkIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleMarkAsContacted(info.getValue())}
            className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
            title="Marquer contacté"
          >
            <PhoneIcon className="h-4 w-4" />
          </button>
        </div>
      ),
    }),
  ];

  const tableData: RowObj[] = React.useMemo(() => {
    if (!flatData) return [];
    return flatData.map((invoice) => ({
      _id: invoice._id,
      clientId: invoice.clientId,
      clientName: invoice.client?.name ?? "-",
      createdAt: invoice.createdAt,
      dueDate: invoice.dueDate,
      lastContact: invoice.lastContact,
      total: invoice.totalTTC,
      dueAmount: invoice.dueAmount ?? invoice.totalHT,
    }));
  }, [flatData]);

  const table = useReactTable({
    data: tableData,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-gray-900">En retard</h3>
          <p className="text-xs text-gray-400">
            Factures dont l'échéance est dépassée
          </p>
        </div>
        {totalPage > 0 && (
          <span className="inline-flex items-center rounded-full bg-red-50 px-2.5 py-0.5 text-xs font-medium text-red-700">
            {totalPage} page{totalPage > 1 ? "s" : ""}
          </span>
        )}
      </div>

      <div className="px-5 pt-3">
        <SearchAndFilterBar
          searchValue={searchName}
          onSearchChange={setSearchName}
          onFilterChange={setFilters}
          type="invoice"
        />
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-gray-600" />
        </div>
      ) : tableData.length === 0 ? (
        <div className="py-12 text-center">
          <p className="text-sm text-gray-400">Aucune facture en retard</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id}>
                    {hg.headers.map((header) => (
                      <th
                        key={header.id}
                        onClick={header.column.getToggleSortingHandler()}
                        className="cursor-pointer px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-400"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className="divide-y divide-gray-50">
                {table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className="transition-colors hover:bg-gray-50/50"
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="whitespace-nowrap px-5 py-3 text-sm">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 px-5 py-3">
            <span className="text-xs text-gray-400">
              Page {currentPage} sur {totalPage}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() =>
                  currentPage > 1 && setCurrentPage(currentPage - 1)
                }
                disabled={currentPage <= 1}
                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"
              >
                <MdArrowLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => {
                  if (hasNextPage) {
                    fetchNextPage();
                    setCurrentPage(currentPage + 1);
                  }
                }}
                disabled={!hasNextPage}
                className="rounded-md p-1.5 text-gray-400 transition-colors hover:bg-gray-100 disabled:opacity-30"
              >
                <MdArrowRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
